<template>
  <div class="details-form">
    <div class="sm:border-b sm:border-gray-400 sm:pb-5 mb-5">
      <h3 class="text-lg leading-6 font-semibold text-blue-600">
        {{ title || 'Package Details' }}
      </h3>
    </div>

    <div class="grid grid-cols-12 gap-6 sm:mb-5">
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="batch_name"> Harvest Batch Name </label>
        <input
          type="text"
          id="batch_name"
          name="name"
          v-model="form.name"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="name"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="batch_num"> Batch # </label>
        <input
          type="text"
          id="batch_num"
          name="batch_num"
          v-model="form.batch_num"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="batch_num"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="tag_num"> Tag # </label>
        <input
          type="text"
          id="tag_num"
          name="tag_num"
          v-model="form.tag_num"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="tag_num"
        />
      </div>

      <div
        v-if="hasPackage"
        class="input-group col-span-12 sm:col-span-4"
      >
        <label for="batch_name mb-1"> Product Assignment </label>
        <div class="sub-group relative flex items-stretch grow focus-within:z-10">
          <input
            type="text"
            name="name"
            :value="form.product_name || ''"
            disabled="disabled"
          />
          <button
            v-if="form.product"
            @click="goToAssignedProduct()"
            type="button"
            class="absolute top-0 right-0 items-center px-4 py-2 border border-gray-400 text-sm font-medium rounded-r-md bg-gray-100 hover:bg-gray-400 h-full"
          >
            <EyeIcon
              class="h-5 w-5 text-gray-700"
              aria-hidden="true"
            />
          </button>
          <error
            :errors="errors"
            name="name"
          />
        </div>
      </div>
      <div
        v-else
        class="input-group col-span-12 sm:col-span-4"
      >
        <label for="batch_name mb-1"> Product Assignment </label>
        <ComboBox
          :data="products"
          :filter-on="'name'"
          :label="(item) => (item ? `${item.name}` : 'Search for Product')"
          :current-selection="form.product_id ? products.find((product) => product.id === form.product_id) : null"
          @update="(product) => (form.product_id = product.id)"
        />
      </div>

      <div
        v-if="hasPackage"
        class="input-group col-span-12 sm:col-span-4"
      >
        <label>Origin Location:</label>
        <div class="sub-group relative flex items-stretch grow focus-within:z-10">
          <input
            type="text"
            name="name"
            :value="package.location.name || ''"
            disabled="disabled"
          />
        </div>
      </div>
      <div
        v-else
        class="input-group col-span-12 sm:col-span-4"
      >
        <label>Origin Location:</label>
        <select
          v-model="form.location_id"
          name="location_id"
        >
          <template v-for="location in locationList">
            <option :value="location.id">{{ location.name }}</option>
          </template>
        </select>
        <error
          :errors="errors"
          name="location_id"
        />
      </div>

      <div
        v-if="hasPackage"
        class="input-group col-span-12 sm:col-span-4"
      >
        <label>Origin Space:</label>
        <div class="sub-group relative flex items-stretch grow focus-within:z-10">
          <input
            type="text"
            name="name"
            :value="package.space.name || ''"
            disabled="disabled"
          />
        </div>
      </div>
      <div
        v-else
        class="input-group col-span-12 sm:col-span-4"
      >
        <label>Space:</label>
        <select
          v-model="form.space_id"
          name="space_id"
        >
          <option :value="null">Select a Space</option>
          <template v-for="space in spaceList">
            <option :value="space.id">{{ space.name }}</option>
          </template>
        </select>
        <error
          :errors="errors"
          name="space_id"
        />
      </div>

      <div class="input-group col-span-12 sm:col-span-4">
        <label for="batch_num"> Item # </label>
        <input
          type="text"
          id="item_num"
          name="item_num"
          v-model="form.item_num"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="item_num"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="batch_num"> Case # </label>
        <input
          type="text"
          id="case_num"
          name="case_num"
          v-model="form.case_num"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="case_num"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="lot_num"> Lot # </label>
        <input
          type="text"
          id="lot_num"
          name="lot_num"
          v-model="form.lot_num"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="lot_num"
        />
      </div>
      <div
        :class="{ 'sm:col-span-3': hasPackage, 'sm:col-span-4': !hasPackage }"
        class="input-group col-span-12"
      >
        <label for="total_qty"> Total Qty </label>
        <input
          type="text"
          name="quantity"
          v-model="form.quantity"
          :disabled="disabled"
        />
        <!--                <div class="mt-1 relative rounded-md shadow-sm">-->
        <!--                    <input type="text" id="quantity" name="quantity" v-model="form.quantity" :disabled="disabled"/>-->
        <!--                    <div class="absolute inset-y-0 right-0 flex items-center">-->
        <!--                        <label for="available_uom" class="sr-only">UoM</label>-->
        <!--                        <select id="available_uom"-->
        <!--                                class="focus:ring-indigo-500 focus:border-indigo-500 h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-600 sm:text-sm rounded-md">-->
        <!--                            <option value="qty">qty</option>-->
        <!--                            <option value="g">g.</option>-->
        <!--                            <option value="mg">mg.</option>-->
        <!--                            <option value="oz">oz.</option>-->
        <!--                            <option value="lb">lb.</option>-->
        <!--                        </select>-->
        <!--                    </div>-->
        <!--                </div>-->
        <error
          :errors="errors"
          name="quantity"
        />
      </div>
      <div
        :class="{ 'sm:col-span-3': hasPackage, 'sm:col-span-4': !hasPackage }"
        class="input-group col-span-12"
      >
        <label for="available_qty"> Stock On Hand </label>
        <input
          type="text"
          name="quantity_on_hand"
          v-model="form.quantity_on_hand_at_source"
          :disabled="true"
        />
        <error
          :errors="errors"
          name="quantity_available"
        />
      </div>
      <div
        :class="{ 'sm:col-span-3': hasPackage, 'sm:col-span-4': !hasPackage }"
        class="input-group col-span-12"
      >
        <label for="available_qty"> Available Qty </label>
        <input
          type="text"
          id="available_qty"
          name="quantity_available_total"
          v-model="form.quantity_available_at_source"
          :disabled="true"
        />
        <error
          :errors="errors"
          name="quantity_available"
        />
      </div>
      <div
        v-if="hasPackage"
        class="input-group col-span-12 sm:col-span-3"
      >
        <label for="is_published"> Status </label>
        <select
          id="is_published"
          name="is_published"
          v-model="form.is_published"
          :disabled="disabled || !hasPackage"
        >
          <option :value="true">Published</option>
          <option :value="false">Draft</option>
        </select>
        <error
          :errors="errors"
          name="lineage"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="approval_date"> COA Approval Date </label>
        <input
          type="date"
          id="coa_approved_at"
          name="coa_approved_at"
          v-model="formattedCOAApprovalDate"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="coa_approved_at"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="exp_date"> Expiration Date </label>
        <input
          type="date"
          id="expires_at"
          name="expires_at"
          v-model="formattedExpirationDate"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="expires_at"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-4">
        <label for="lineage"> Lineage <small>override</small> </label>
        <select
          id="lineage"
          name="lineage"
          v-model="form.lineage"
          :disabled="disabled"
        >
          <option :value="null">Select Lineage</option>
          <option
            v-for="(label, key) in lineages"
            :key="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
        <error
          :errors="errors"
          name="lineage"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-12">
        <label for="excerpt"> Excerpt <small>override</small> </label>
        <textarea
          type="text"
          id="excerpt"
          rows="5"
          name="excerpt"
          v-model="form.excerpt"
          :disabled="disabled"
        ></textarea>
        <error
          :errors="errors"
          name="excerpt"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-12">
        <label for="description"> Description <small>override</small> </label>
        <textarea
          type="text"
          id="description"
          rows="5"
          name="description"
          v-model="form.description"
          :disabled="disabled"
        ></textarea>
        <error
          :errors="errors"
          name="description"
        />
      </div>
    </div>

    <div class="sm:border-b sm:border-gray-400 sm:pb-5 my-5">
      <h3 class="text-lg leading-6 font-semibold text-blue-600">Terpene Totals</h3>
    </div>

    <div class="grid grid-cols-12 gap-6 sm:mb-5">
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="total_thc"> Total THC </label>
        <input
          type="number"
          min="0"
          id="total_thc"
          name="cannabinoids[total_thc]"
          v-model="form.cannabinoids.total_thc"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="cannabinoids.total_thc"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="total_thc"> THCA </label>
        <input
          type="number"
          min="0"
          id="thca"
          name="cannabinoids[total_thc]"
          v-model="form.cannabinoids.thca"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="cannabinoids.thca"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="total_cbd"> Total CBD </label>
        <input
          type="number"
          min="0"
          id="total_cbd"
          name="cannabinoids[total_cbd]"
          v-model="form.cannabinoids.total_cbd"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="cannabinoids.total_cbd"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="total_terpenes"> Total Terpenes </label>
        <input
          type="number"
          min="0"
          id="total_terpenes"
          name="terpenes[total]"
          v-model="form.terpenes.total"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.total"
        />
      </div>
    </div>

    <div class="sm:border-b sm:border-gray-400 sm:pb-5 my-5">
      <h3 class="text-lg leading-6 font-semibold text-blue-600">Terpene Details</h3>
    </div>

    <div class="grid grid-cols-12 gap-6 sm:mb-5">
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="pinene"> alpha-Pinene </label>
        <input
          type="number"
          min="0"
          id="alpha_pinene"
          name="terpenes[alpha_pinene]"
          v-model="form.terpenes.alpha_pinene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.alpha_pinene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="alpha_bisabolol"> alpha-Bisabolol </label>
        <input
          type="number"
          min="0"
          id="alpha_bisabolol"
          name="terpenes[alpha_bisabolol]"
          v-model="form.terpenes.alpha_bisabolol"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.alpha_bisabolol"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="pinene"> beta-Pinene </label>
        <input
          type="number"
          min="0"
          id="beta_pinene"
          name="terpenes.beta_pinene]"
          v-model="form.terpenes.beta_pinene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.beta_pinene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="borneol"> Borneol </label>
        <input
          type="number"
          min="0"
          id="borneol"
          name="terpenes[borneol]"
          v-model="form.terpenes.borneol"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.borneol"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="camphene"> Camphene </label>
        <input
          type="number"
          min="0"
          id="camphene"
          name="terpenes[camphene]"
          v-model="form.terpenes.camphene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.camphene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="caryophyllene"> Caryophyllene </label>
        <input
          type="number"
          min="0"
          id="caryophyllene"
          name="terpenes[caryophyllene]"
          v-model="form.terpenes.caryophyllene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.caryophyllene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="carene"> Delta 3 Carene </label>
        <input
          type="number"
          min="0"
          id="carene"
          name="terpenes[delta_3_carene]"
          v-model="form.terpenes.delta_3_carene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.delta_3_carene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="eucalyptol"> Eucalyptol </label>
        <input
          type="number"
          min="0"
          id="eucalyptol"
          name="terpenes[eucalyptol]"
          v-model="form.terpenes.eucalyptol"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.eucalyptol"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="geraniol"> Geraniol </label>
        <input
          type="number"
          min="0"
          id="geraniol"
          name="terpenes[geraniol]"
          v-model="form.terpenes.geraniol"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.geraniol"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="guaiol"> Guaiol </label>
        <input
          type="number"
          min="0"
          id="guaiol"
          name="terpenes[guaiol]"
          v-model="form.terpenes.guaiol"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.guaiol"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="farnesene"> Farnesene </label>
        <input
          type="number"
          min="0"
          id="farnesene"
          name="terpenes[farnesene]"
          v-model="form.terpenes.farnesene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.farnesene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="humulene"> Humulene </label>
        <input
          type="number"
          min="0"
          id="humulene"
          name="terpenes[humulene]"
          v-model="form.terpenes.humulene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.humulene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="limonene"> Limonene </label>
        <input
          type="number"
          min="0"
          id="limonene"
          name="terpenes[limonene]"
          v-model="form.terpenes.limonene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.limonene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="linalool"> Linalool </label>
        <input
          type="number"
          min="0"
          id="linalool"
          name="terpenes[linalool]"
          v-model="form.terpenes.linalool"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.linalool"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="myrcene"> Myrcene </label>
        <input
          type="number"
          min="0"
          id="myrcene"
          name="terpenes[myrcene]"
          v-model="form.terpenes.myrcene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.myrcene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="ocimene"> Ocimene </label>
        <input
          type="number"
          min="0"
          id="ocimene"
          name="terpenes[ocimene]"
          v-model="form.terpenes.ocimene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.ocimene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="terpineol"> Terpineol </label>
        <input
          type="number"
          min="0"
          id="terpineol"
          name="terpenes[terpineol]"
          v-model="form.terpenes.terpineol"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.terpineol"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="terpinolene"> Terpinolene </label>
        <input
          type="number"
          min="0"
          id="terpinolene"
          name="terpenes[terpinolene]"
          v-model="form.terpenes.terpinolene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.terpinolene"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="trans_nerolidol"> trans-Nerolidol </label>
        <input
          type="number"
          min="0"
          id="trans_nerolidol"
          name="terpenes[trans_nerolidol]"
          v-model="form.terpenes.trans_nerolidol"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.trans_nerolidol"
        />
      </div>
      <div class="input-group col-span-12 sm:col-span-3">
        <label for="valencene"> Valencene </label>
        <input
          type="number"
          min="0"
          id="valencene"
          name="terpenes[valencene]"
          v-model="form.terpenes.valencene"
          :disabled="disabled"
        />
        <error
          :errors="errors"
          name="terpenes.valencene"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Error from '@/Components/Forms/Error'
import moment from 'moment'
import { EyeIcon } from '@heroicons/vue/outline'
import ComboBox from '@/Components/Forms/ComboBox.vue'

export default {
  name: 'details-form',

  components: { ComboBox, Error, EyeIcon },

  props: {
    /**
     * The title that appears at the top of the form
     */
    title: {
      type: String,
      default: null
    },
    /**
     * A key val list of errors
     */
    errors: {
      type: Object,
      default() {
        return {}
      }
    },
    /**
     * (v-model) A copy of the form we are filling out
     */
    modeValue: {
      type: Object,
      default() {
        return {}
      }
    },
    /**
     * The Package we want to display or create
     */
    package: {
      type: Object,
      default() {
        return {}
      }
    },

    /**
     * Enables/Disables all form fields
     */
    disabled: {
      type: Boolean,
      default: false
    },
    locations: {
      type: Array,
      default() {
        return []
      }
    },
    spaces: {
      type: Array,
      default() {
        return []
      }
    },
    /**
     * A list of locations we can transfer a package to
     */
    products: {
      type: Array,
      default() {
        return []
      }
    },
    initialProductId: {
      type: Number,
      default: null
    },
    /**
     * A key val list of lineages for selection
     */
    lineages: {
      type: Object,
      default() {
        return {}
      }
    }
  },

  watch: {
    form: {
      deep: true,
      handler() {
        this.emitForm()
      }
    },
    'form.location_id': function (newVal, oldVal) {
      this.form.space_id = null
    }
  },

  data() {
    return {
      form: {
        location_id: null,
        space_id: null,
        product_id: this.initialProductId,
        ...this.package,
        is_published: !!this.package.published_at,
        terpenes: this.package.terpenes || {},
        cannabinoids: this.package.cannabinoids || {}
      }
    }
  },

  mounted() {
    this.$emit('update:modelValue', this.form)
  },

  computed: {
    hasPackage() {
      return !_.isEmpty(this.package)
    },

    formattedExpirationDate: {
      get() {
        return moment.utc(this.form.expires_at)?.format('YYYY-MM-DD')
      },
      set(value) {
        this.form.expires_at = value
      }
    },

    formattedCOAApprovalDate: {
      get() {
        return moment.utc(this.form.coa_approved_at)?.format('YYYY-MM-DD')
      },
      set(value) {
        this.form.coa_approved_at = value
      }
    },
    /**
     * Returns a list of products with a default value at the beginning
     */
    productList() {
      return [{ name: 'Select A Product', id: null }, ...this.products]
    },
    locationList() {
      return [{ name: `Select a Location`, id: null }, ...this.locations]
    },
    spaceList() {
      if (!this.form.location_id) {
        return []
      }

      return this.spaces.filter((space) => space.location_id === this.form.location_id)
    }
  },

  methods: {
    /**
     * Emits form to parent component
     */
    emitForm() {
      //this.updateForm(this.form);
      this.$emit('update:modelValue', this.form)
    },
    goToAssignedProduct() {
      if (!this.form.product) {
        return
      }

      this.$inertia.visit(route('products.edit', { product: this.form.product.id }))
    }
  }
}
</script>
